import { ref } from 'vue';
import { db } from '@punchlog/core-tracker/src/database.js';
import { useGoogleDriveSync, driveSignIn, driveSignOut } from './useGoogleDriveSync.js';
import { useOneDriveSync, oneDriveSignIn, oneDriveSignOut } from './useOneDriveSync.js';

const SYNC_FILE   = 'punchlog-data.json';
const POLL_MS     = 5 * 60_000; // background pull from other devices
const DEBOUNCE_MS = 5_000;      // wait after last local mutation before uploading
const LAST_HASH_KEY = 'sync_last_hash';

const TABLES = { timeEntries: 'id', workplaces: 'id', schedules: 'id', signedBundles: 'bundle_id' };

export const provider   = ref(localStorage.getItem('sync_provider') ?? null);
export const lastSyncAt = ref(localStorage.getItem('sync_last_at') ?? null);
export const syncError   = ref(null);
export const isSyncing   = ref(false);
export const needsReauth = ref(false);
// Fires when a sync brings in new remote data so views can reload
export const syncImported = ref(0);

let _upload        = null;
let _download      = null;
let _signIn        = null;
let _signOut       = null;
let _pollTimer     = null;
let _debounceTimer = null;
let _hooksAttached = false;
// Set true during mergeData writes so hooks don't re-trigger a sync loop
let _isMergingRemote = false;

// ── Helpers ───────────────────────────────────────────────────────────────────
export async function hashData(data) {
  const bytes = new TextEncoder().encode(JSON.stringify(data));
  const buf   = await crypto.subtle.digest('SHA-256', bytes);
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

function scheduleDirtySync() {
  if (!_upload || _isMergingRemote) return;
  clearTimeout(_debounceTimer);
  _debounceTimer = setTimeout(() => sync().catch(() => {}), DEBOUNCE_MS);
}

function attachHooks() {
  if (_hooksAttached) return;
  _hooksAttached = true;
  for (const table of Object.keys(TABLES)) {
    if (!db[table]) continue;
    ['creating', 'updating', 'deleting'].forEach(event => {
      db[table].hook(event, () => {
        // Defer outside the current transaction so the write commits first
        setTimeout(scheduleDirtySync, 0);
      });
    });
  }
}

function bindProvider(p) {
  if (p === 'google') {
    const g = useGoogleDriveSync();
    _upload = g.uploadData; _download = g.downloadData;
    _signIn = driveSignIn;  _signOut  = driveSignOut;
  } else if (p === 'onedrive') {
    const o = useOneDriveSync();
    _upload = o.uploadData; _download = o.downloadData;
    _signIn = oneDriveSignIn; _signOut = oneDriveSignOut;
  } else {
    _upload = _download = _signIn = _signOut = null;
  }
}

async function exportData() {
  const snap = {};
  for (const table of Object.keys(TABLES)) {
    snap[table] = await db[table]?.toArray().catch(() => []) ?? [];
  }
  return { exportedAt: new Date().toISOString(), version: 1, data: snap };
}

// Returns true if any rows were written from remote.
// _isMergingRemote prevents mutation hooks from scheduling a redundant upload.
async function mergeData(remote) {
  if (!remote?.data) return false;
  _isMergingRemote = true;
  let imported = false;
  try {
    for (const [table, key] of Object.entries(TABLES)) {
      if (!db[table]) continue;
      for (const row of remote.data[table] ?? []) {
        if (!row[key]) continue;
        const existing = await db[table].get(row[key]).catch(() => null);
        if (!existing) {
          await db[table].put(row).catch(() => {});
          imported = true;
        } else {
          const remoteTs = new Date(row.modified_timestamp ?? row.updated_at ?? row.created_at ?? 0).getTime();
          const localTs  = new Date(existing.modified_timestamp ?? existing.updated_at ?? existing.created_at ?? 0).getTime();
          if (remoteTs > localTs) {
            await db[table].put(row).catch(() => {});
            imported = true;
          }
        }
      }
    }
  } finally {
    _isMergingRemote = false;
  }
  return imported;
}

export async function sync() {
  if (!_upload || isSyncing.value) return;
  isSyncing.value = true;
  syncError.value = null;
  try {
    const remote   = await _download(SYNC_FILE);
    const imported = await mergeData(remote);
    const merged   = await exportData();

    // Skip upload when local data hasn't changed (hash only data, not exportedAt)
    const hash = await hashData(merged.data);
    if (hash !== localStorage.getItem(LAST_HASH_KEY)) {
      await _upload(SYNC_FILE, merged);
      localStorage.setItem(LAST_HASH_KEY, hash);
    }

    lastSyncAt.value = merged.exportedAt;
    localStorage.setItem('sync_last_at', lastSyncAt.value);
    needsReauth.value = false;
    if (imported) syncImported.value++;
  } catch (e) {
    if (e.message === 'token_expired') {
      needsReauth.value = true;
      return; // silent — don't surface as error, don't throw
    }
    syncError.value = e.message;
    throw e;
  } finally {
    isSyncing.value = false;
  }
}

export async function connectProvider(p) {
  bindProvider(p);
  try {
    await _signIn();
  } catch (e) {
    // Sign-in failed — don't persist provider state
    bindProvider(null);
    throw e;
  }
  provider.value = p;
  needsReauth.value = false;
  localStorage.setItem('sync_provider', p);
  attachHooks();
  await sync().catch(() => {});
  startPolling();
}

export async function disconnectProvider() {
  stopPolling();
  clearTimeout(_debounceTimer);
  _debounceTimer = null;
  if (_signOut) await _signOut().catch(() => {});
  provider.value = null;
  needsReauth.value = false;
  localStorage.removeItem('sync_provider');
  localStorage.removeItem('sync_last_at');
  localStorage.removeItem(LAST_HASH_KEY);
  lastSyncAt.value = null;
  _upload = _download = _signIn = _signOut = null;
}

export function startPolling() {
  stopPolling();
  if (!provider.value) return;
  _pollTimer = setInterval(() => sync().catch(() => {}), POLL_MS);
}

export function stopPolling() {
  if (_pollTimer) { clearInterval(_pollTimer); _pollTimer = null; }
}

export function initDriveSync() {
  if (provider.value) {
    bindProvider(provider.value);
    attachHooks();
    startPolling();
  }
}

export function useDriveSync() {
  return { provider, lastSyncAt, syncError, isSyncing, syncImported, needsReauth, sync, connectProvider, disconnectProvider };
}
